import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MoreHorizontal } from 'lucide-react';
import { useCreatedVsResolved } from '../../hooks/useDashboard';
import Spinner from '../ui/Spinner';

const PERIOD_OPTIONS = [
  { value: 'daily', label: '일별' },
  { value: 'weekly', label: '주별' },
  { value: 'monthly', label: '월별' },
];

type ChartMode = 'daily-count' | 'cumulative';
type RangeMode = 'all' | 'days-ago';

interface ChartSettings {
  period: string;
  chartMode: ChartMode;
  rangeMode: RangeMode;
  daysAgo: number;
}

const STORAGE_KEY_PREFIX = 'maios-cvr-settings-';

function loadSettings(projectId: string): Partial<ChartSettings> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + projectId);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function saveSettings(projectId: string, settings: ChartSettings) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + projectId, JSON.stringify(settings));
  } catch { /* ignore */ }
}

interface Props {
  projectId: string;
  projectCreatedAt: string;
}

export default function CreatedVsResolvedWidget({ projectId, projectCreatedAt }: Props) {
  const saved = useRef(loadSettings(projectId)).current;
  const [period, setPeriod] = useState(saved.period ?? 'daily');
  const [chartMode, setChartMode] = useState<ChartMode>(saved.chartMode ?? 'daily-count');
  const [rangeMode, setRangeMode] = useState<RangeMode>(saved.rangeMode ?? 'days-ago');
  const [daysAgo, setDaysAgo] = useState(saved.daysAgo ?? 30);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 설정 변경 시 localStorage 저장
  const persistSettings = useCallback(() => {
    saveSettings(projectId, { period, chartMode, rangeMode, daysAgo });
  }, [projectId, period, chartMode, rangeMode, daysAgo]);

  useEffect(() => {
    persistSettings();
  }, [persistSettings]);

  // 메뉴 외부 클릭 닫기
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  // 날짜 범위 계산
  const { from, to } = useMemo(() => {
    const toDate = new Date().toISOString().slice(0, 10);
    if (rangeMode === 'all') {
      return { from: new Date(projectCreatedAt).toISOString().slice(0, 10), to: toDate };
    }
    const fromDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    return { from: fromDate, to: toDate };
  }, [rangeMode, daysAgo, projectCreatedAt]);

  const { data, isLoading } = useCreatedVsResolved(projectId, period, from, to);

  // 누적 데이터 변환
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return data;
    if (chartMode === 'daily-count') return data;

    let cumulatedCreated = 0;
    let cumulatedResolved = 0;
    return data.map((item) => {
      cumulatedCreated += item.created;
      cumulatedResolved += item.resolved;
      return { period: item.period, created: cumulatedCreated, resolved: cumulatedResolved };
    });
  }, [data, chartMode]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full"><Spinner className="size-6" /></div>;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          생성 vs 해결 추이
          <span className="ml-1.5 text-[11px] font-normal text-gray-400">
            {chartMode === 'cumulative' ? '(누적)' : '(건수)'}
          </span>
        </h3>
        <div className="flex items-center gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-2 py-0.5 text-[11px] rounded ${
                period === opt.value
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {opt.label}
            </button>
          ))}

          {/* 더보기 버튼 */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <MoreHorizontal className="size-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 p-3 space-y-3">
                {/* 차트 모드 */}
                <div>
                  <p className="text-[11px] font-medium text-gray-500 mb-1.5">차트 표시</p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setChartMode('daily-count')}
                      className={`flex-1 px-2 py-1 text-sm rounded ${
                        chartMode === 'daily-count'
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      날짜 별 건수
                    </button>
                    <button
                      onClick={() => setChartMode('cumulative')}
                      className={`flex-1 px-2 py-1 text-sm rounded ${
                        chartMode === 'cumulative'
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-gray-500 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      누적 건수
                    </button>
                  </div>
                </div>

                {/* 날짜 범위 */}
                <div>
                  <p className="text-[11px] font-medium text-gray-500 mb-1.5">표시 기간</p>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rangeMode"
                        checked={rangeMode === 'all'}
                        onChange={() => setRangeMode('all')}
                        className="size-3.5 text-primary-600"
                      />
                      <span className="text-sm text-gray-700">전체 (프로젝트 생성일~오늘)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rangeMode"
                        checked={rangeMode === 'days-ago'}
                        onChange={() => setRangeMode('days-ago')}
                        className="size-3.5 text-primary-600"
                      />
                      <span className="text-sm text-gray-700">이전 날짜</span>
                      {rangeMode === 'days-ago' && (
                        <span className="flex items-center gap-1">
                          <input
                            type="number"
                            min={1}
                            max={365}
                            value={daysAgo}
                            onChange={(e) => setDaysAgo(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                            className="w-14 px-1.5 py-0.5 text-sm border border-gray-300 rounded text-center focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                          />
                          <span className="text-[11px] text-gray-500">일 전</span>
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(!chartData || chartData.length === 0) ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">데이터 없음</div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" allowDecimals={false} />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value, name) => [
                `${value}건`,
                (name as string) === 'created' ? '생성' : '해결',
              ]}
            />
            <Legend
              formatter={(value: string) => (
                <span className="text-sm">{value === 'created' ? '생성' : '해결'}</span>
              )}
            />
            <Line type="monotone" dataKey="created" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="resolved" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
